/**
 * Two-tap call outcome: Reached / Not Reached, then an outcome from the template, a note, a callback time when the
 * outcome asks for one, and a do-not-call confirm when the outcome writes a suppression.
 *
 * Controlled: the caller holds the `CallOutcomeValue`, saves through its own endpoint and gates its Save on
 * `outcomeReady`. Seed it with `machineSuggestion` after a click-to-dial call so RingCentral's answer is pre-picked;
 * `suggested` then says so beside the taps. `steps="note"` renders the note alone, for a surface that stores no
 * outcome yet.
 */
import { useId, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Kbd } from "@/components/ui/kbd";
import { LocalDatePicker } from "@/components/ui/local-date-picker";
import { Textarea } from "@/components/ui/textarea";
import { TimePicker12 } from "@/components/ui/time-picker";
import { cn } from "@/lib/utils";

import {
  type CallOutcomeValue,
  type CallReach,
  machineSuggestion,
  type MachineCallOutcome,
  NOTE_ONLY,
  optionFor,
  outcomeReady,
  type OutcomeTemplate,
  REACH_LABELS,
} from "./call-outcomes";

const MAX_NOTE = 2000;

function Tap({
  pressed,
  disabled,
  onClick,
  children,
  className,
}: {
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={pressed ? "default" : "outline"}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn("min-h-11 md:min-h-8", className)}
    >
      {children}
    </Button>
  );
}

function KeyHint({ pressed, children }: { pressed: boolean; children: React.ReactNode }) {
  return (
    <Kbd tone={pressed ? "onPrimary" : "muted"} deviceOnly className="ml-1.5" aria-hidden="true">
      {children}
    </Kbd>
  );
}

export function CallOutcomeCapture({
  template = NOTE_ONLY,
  value,
  onChange,
  steps = "outcome",
  suggested = false,
  disabled = false,
  dncName = "this customer",
  notePlaceholder = "What happened on the call? (optional)",
  noteClassName,
  reachKeys,
  outcomeKeys,
}: {
  /** The outcomes to offer; omit with `steps="note"`. */
  template?: OutcomeTemplate;
  value: CallOutcomeValue;
  onChange: (next: CallOutcomeValue) => void;
  steps?: "outcome" | "note";
  /** The reach was pre-picked from the call; shown as a quiet line under the taps. */
  suggested?: boolean;
  disabled?: boolean;
  /** Who the do-not-call confirm names. */
  dncName?: string;
  notePlaceholder?: string;
  noteClassName?: string;
  /** Key hints on the taps, for a screen that handles those keys itself (the mission call screen). */
  reachKeys?: Partial<Record<CallReach, string>>;
  outcomeKeys?: Record<string, string>;
}) {
  const noteId = useId();
  const option = optionFor(template, value);
  const options = value.reach ? template[value.reach] : [];
  const set = (patch: Partial<CallOutcomeValue>) => onChange({ ...value, ...patch });
  const pickReach = (reach: CallReach) =>
    set({ reach, outcome: reach === value.reach ? value.outcome : null, dncConfirmed: false });

  return (
    <div className="space-y-3" data-testid="call-outcome-capture">
      {steps === "outcome" ? (
        <>
          <div className="space-y-1.5">
            <div role="group" aria-label="Did you reach them?" className="grid grid-cols-2 gap-2">
              {(["reached", "not_reached"] as const).map((reach) => (
                <Tap key={reach} pressed={value.reach === reach} disabled={disabled} onClick={() => pickReach(reach)}>
                  {REACH_LABELS[reach]}
                  {reachKeys?.[reach] ? <KeyHint pressed={value.reach === reach}>{reachKeys[reach]}</KeyHint> : null}
                </Tap>
              ))}
            </div>
            {suggested && value.reach ? (
              <p className="text-xs text-muted-foreground">Picked from the call. Change it if that's wrong.</p>
            ) : null}
          </div>

          {options.length > 0 ? (
            <div role="group" aria-label="Outcome" className="flex flex-wrap gap-2">
              {options.map((o) => (
                <Tap
                  key={o.value}
                  pressed={value.outcome === o.value}
                  disabled={disabled}
                  onClick={() => set({ outcome: o.value, dncConfirmed: false })}
                >
                  {o.label}
                  {outcomeKeys?.[o.value] ? (
                    <KeyHint pressed={value.outcome === o.value}>{outcomeKeys[o.value]}</KeyHint>
                  ) : null}
                </Tap>
              ))}
            </div>
          ) : null}

          {option?.callback ? (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Call back at">
              <span className="text-xs font-medium text-muted-foreground">Call back</span>
              <LocalDatePicker
                value={value.callbackDate}
                onChange={(callbackDate) => set({ callbackDate })}
                placeholder="Pick a day"
                buttonSize="sm"
                buttonAriaLabel="Callback day"
                showToday
                disabled={disabled}
              />
              <TimePicker12
                value={value.callbackTime}
                onChange={(callbackTime) => set({ callbackTime })}
                disabled={disabled}
              />
            </div>
          ) : null}

          {option?.dnc ? (
            <label className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm">
              <Checkbox
                checked={value.dncConfirmed}
                onCheckedChange={(checked) => set({ dncConfirmed: checked === true })}
                disabled={disabled}
                className="mt-0.5"
              />
              <span>Yes, stop calling {dncName}. No one will call this number again until someone lifts it.</span>
            </label>
          ) : null}
        </>
      ) : null}

      <div className="space-y-1">
        <label htmlFor={noteId} className="sr-only">
          Call notes
        </label>
        <Textarea
          id={noteId}
          value={value.note}
          onChange={(e) => set({ note: e.target.value })}
          rows={3}
          maxLength={MAX_NOTE}
          placeholder={notePlaceholder}
          disabled={disabled}
          className={cn("resize-none text-base md:text-sm", noteClassName)}
        />
      </div>
    </div>
  );
}

/**
 * The capture with its own state and a Save, for a screen that has no save of its own (the click-to-dial dialog
 * once the call ends). Starts from RingCentral's answer; Save stays off until every tap the template asks for is
 * made, and shows busy while `onSubmit` runs. A rejected `onSubmit` keeps the form and its values for a retry;
 * the caller says what went wrong.
 */
export function CallOutcomeForm({
  template,
  machine,
  onSubmit,
  submitLabel = "Save outcome",
  dncName,
}: {
  template: OutcomeTemplate;
  machine: MachineCallOutcome | null;
  onSubmit: (value: CallOutcomeValue) => Promise<void> | void;
  submitLabel?: string;
  dncName?: string;
}) {
  const [value, setValue] = useState(() => machineSuggestion(template, machine));
  const [saving, setSaving] = useState(false);
  const ready = outcomeReady(template, value);
  const save = async () => {
    setSaving(true);
    try {
      await onSubmit(value);
    } catch {
      // Kept open with the rep's values; the caller already showed the error.
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3">
      <CallOutcomeCapture
        template={template}
        value={value}
        onChange={setValue}
        suggested={machine === "reached" || machine === "voicemail"}
        disabled={saving}
        dncName={dncName}
      />
      <div className="flex justify-end">
        <Button onClick={() => void save()} disabled={!ready || saving} aria-busy={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {saving ? "Saving…" : submitLabel}
        </Button>
      </div>
    </div>
  );
}
